import { useTranslation } from 'react-i18next';

import { Sheet } from '@/components/common/Sheet';
import { type Egg, eggColorKey, eggNameKey } from '@/lib/eggs';
import { cn } from '@/lib/cn';

/**
 * «Hai ottenuto un uovo» — il momento in cui l'uovo diventa tuo.
 *
 * Vive in un componente suo e non dentro il Profilo perché il posto in cui
 * dovrà comparire davvero è un altro: la **Hub**, alla prima apertura dell'app.
 * Oggi lo apre il gesto di prova sull'uovo; domani lo aprirà l'assegnazione
 * vera, senza che questo file cambi.
 *
 * Usa `Sheet` come ogni altro pannello dell'app (ADR-0038, punto 7). La
 * tentazione era una finestra centrata «da premio», ma un gesto diverso per un
 * pannello diverso è esattamente ciò che quella regola evita: chi ha imparato a
 * trascinare via un foglio si blocca davanti a uno che non si trascina.
 */
export function EggReward({
  egg,
  open,
  onClose,
}: {
  egg: Egg;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('general');
  const color = t(eggColorKey(egg.id), { defaultValue: egg.id });

  return (
    <Sheet open={open} onClose={onClose} label={t('egg_reward.title', { defaultValue: 'New egg' })}>
      <div className="flex flex-col items-center gap-3 px-6 pb-2 pt-2 text-center">
        {/* L'uovo grande, e nella sua tinta: il premio è la VARIANTE, e
            annunciarla a parole mostrando un'immagine generica sarebbe come
            aprire un pacchetto e leggere cosa c'era dentro. */}
        <span
          className={cn('flex h-32 items-center justify-center', egg.filterClass)}
          style={{ filter: egg.filter }}
        >
          <img src="/egg.png" alt="" aria-hidden className="h-full w-auto object-contain" />
        </span>

        <h2 className="text-xl font-black uppercase tracking-wide text-brand">
          {t('egg_reward.title', { defaultValue: 'You got an egg' })}
        </h2>

        <p className="max-w-[20rem] text-sm text-fg-muted">
          {t('egg_reward.body', {
            color,
            defaultValue: 'A mysterious {{color}} egg.',
          })}
        </p>

        <p className="text-sm font-bold text-fg">
          {t(eggNameKey(egg.id), { defaultValue: egg.id })}
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full rounded-2xl bg-brand py-3.5 text-sm font-bold uppercase tracking-wide text-brand-fg shadow-lg shadow-brand/20 transition-opacity active:opacity-80"
        >
          {t('egg_reward.cta', { defaultValue: 'Claim' })}
        </button>
      </div>
    </Sheet>
  );
}
